import { NgIconsModule } from '@ng-icons/core';
import * as SentryAngular from '@sentry/angular';
import * as Sentry from '@sentry/capacitor';
import { QueryClient, provideTanStackQuery } from '@tanstack/angular-query-experimental';
import { withDevtools } from '@tanstack/angular-query-experimental/devtools';
import { NgxCurrencyInputMode, provideEnvironmentNgxCurrency } from 'ngx-currency';

import { environment } from '@environments/environment';

import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, NgModule, inject, provideZoneChangeDetection } from '@angular/core';
import { MAT_DATE_FORMATS, MatDateFormats } from '@angular/material/core';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MatIconRegistry } from '@angular/material/icon';
import { MAT_RADIO_DEFAULT_OPTIONS } from '@angular/material/radio';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { BrowserModule, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';

import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';

import { provideOverhaulMockBackend } from '@views/investor/queries/overhaul-mock.client';
import { AuthService } from '@views/misc/auth/auth.service';
import { NativeAuthService } from '@views/misc/auth/native-auth.service';
import { StorageService } from '@views/misc/auth/storage.service';

import { PrivacyScreenComponent } from '@components/privacy-screen/privacy-screen.component';
import { SnackBarService } from '@components/sq-snackbar/sq-snackbar.service';

import { MatDayjsDateModule } from '@core/material-dayjs-adapter';
import { ApiConfiguration } from '@core/schemas/ola/api-configuration';
import { AnalyticsService } from '@core/utilities/analytics.service';
import { BrazeService } from '@core/utilities/braze.service';
import { LoggerModule } from '@core/utilities/logger/logger.module';

import packageInfo from '../../package.json';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { appIcons } from './app.icons';
import { HttpErrorInterceptor } from './core/utilities/networkLayer/interceptor.http';
import { openApiFetchProviders } from './core/utilities/networkLayer/openapi-fetch.providers';
import * as redux from './reducers';

export const MAT_DAYJS_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

export const customNgxCurrencyConfig = {
  align: 'left',
  allowNegative: false,
  allowZero: true,
  decimal: '.',
  precision: 2,
  prefix: '',
  suffix: '',
  thousands: ',',
  nullable: true,
  min: null,
  max: null,
  inputMode: NgxCurrencyInputMode.Natural,
};

if (environment.sentryDsn) {
  Sentry.init(
    {
      dsn: environment.sentryDsn,
      environment: environment.name,
      release: packageInfo?.version,
      sendDefaultPii: false,
      ignoreErrors: [
        // In-app browsers (e.g. Instagram) inject bridge scripts that probe window.webkit.messageHandlers
        /window\.webkit\.messageHandlers/,
      ],
      tracesSampleRate: environment.production ? 0.2 : 1.0,
    },
    SentryAngular.init,
  );
}

/** The main entry point for this Squirrel application. */
@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    LoggerModule,
    StoreModule.forRoot(redux.reducers, {
      metaReducers: redux.metaReducers,
      runtimeChecks: {
        strictStateImmutability: true,
        strictActionImmutability: true,
      },
    }),
    EffectsModule.forRoot(redux.effects),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: environment.production,
      connectInZone: true,
    }),
    BrowserAnimationsModule,
    MatDayjsDateModule,
    PrivacyScreenComponent,
    // needed for global use
    MatSnackBarModule,
    MatDialogModule,
    NgIconsModule.withIcons(appIcons),
  ],
  providers: [
    provideZoneChangeDetection(),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    // Angular/CDK 21 renders overlays via the native Popover API (browser top
    // layer), which paints above ALL normal-flow z-index — including the
    // Authsignal MFA popup (a fixed, z-index:2147483647 element on <body>),
    // leaving it stranded behind any open dialog/menu (#234). Opting overlays
    // out of the top layer restores the pre-21 in-container (z-index) stacking,
    // so the MFA popup sits above the menu again.
    { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false } },
    Title,
    AnalyticsService,
    BrazeService,
    AuthService,
    NativeAuthService,
    SnackBarService,
    StorageService,
    { provide: MAT_DATE_FORMATS, useValue: MAT_DAYJS_DATE_FORMATS },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'outline', subscriptSizing: 'dynamic', hideRequiredMarker: true },
    },
    {
      provide: MAT_RADIO_DEFAULT_OPTIONS,
      useValue: { disableRipple: true },
    },
    { provide: ApiConfiguration, useValue: { rootUrl: environment.endpointApiOla } satisfies ApiConfiguration },
    provideEnvironmentNgxCurrency(customNgxCurrencyConfig),
    // needed for global use
    {
      provide: MatDialogRef,
      useValue: {},
    },
    ...(environment.sentryDsn
      ? [
          {
            provide: SentryAngular.TraceService,
            deps: [Router],
          },
          {
            provide: APP_INITIALIZER,
            useFactory: () => () => {},
            deps: [SentryAngular.TraceService],
            multi: true,
          },
        ]
      : []),
    provideHttpClient(withInterceptorsFromDi()),
    provideTanStackQuery(
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30_000,
            // 4xx are client errors that won't self-heal (e.g. a 404 for a resource
            // deleted in another tab), so don't retry them; keep the default 3 retries
            // for 5xx/network. Status is read from the openapi-fetch error body.
            retry: (failureCount, error) => {
              const status = (error as { httpStatus?: number } | undefined)?.httpStatus;
              return status !== undefined && status >= 400 && status < 500 ? false : failureCount < 3;
            },
          },
        },
      }),
      withDevtools(() => ({ loadDevtools: !environment.production, buttonPosition: 'bottom-left' })),
    ),
    ...openApiFetchProviders,
    // TODO: Overhaul — when the accountOverhaul flag is on (dev + staging-ao), override ACCOUNT_OVERHAUL_CLIENT
    // with the single mock backend singleton. No-op (real client) when off. Delete at cut-over.
    ...provideOverhaulMockBackend(),
  ],
})
export class AppModule {
  matIconRegistry = inject(MatIconRegistry);

  constructor() {
    const matIconRegistry = this.matIconRegistry;

    matIconRegistry.registerFontClassAlias('fa-light', 'fa-light');
    matIconRegistry.registerFontClassAlias('fa-solid', 'fa-solid');
  }
}
